import React, { useMemo } from "react";
import type { CSSProperties } from "react";

// Pre-defined fraction widths for visual variety in skeleton rows
const SKELETON_WIDTHS = [
  [0.6, 0.4, 0.5, 0.3, 0.7, 0.45, 0.55, 0.35],
  [0.5, 0.55, 0.3, 0.6, 0.4, 0.7, 0.35, 0.5],
  [0.7, 0.3, 0.6, 0.5, 0.35, 0.55, 0.4, 0.65],
  [0.4, 0.65, 0.45, 0.7, 0.5, 0.3, 0.6, 0.4],
  [0.55, 0.5, 0.7, 0.35, 0.6, 0.45, 0.5, 0.55],
  [0.65, 0.35, 0.55, 0.45, 0.3, 0.6, 0.7, 0.4],
  [0.45, 0.6, 0.4, 0.55, 0.65, 0.35, 0.45, 0.7],
  [0.35, 0.7, 0.5, 0.4, 0.55, 0.5, 0.3, 0.6],
];

const ROW_COUNT = 8;

interface ColumnInfo {
  selector: string;
  width: number;
  isPinned: boolean;
  pinnedStyle: CSSProperties;
}

interface SkeletonRowsProps {
  columns: ColumnInfo[];
}

/**
 * Renders animated skeleton placeholder rows while table data is loading.
 * Each cell has a varied-width pulse animation for visual realism.
 */
const SkeletonRows: React.FC<SkeletonRowsProps> = ({ columns }) => {
  const rows = useMemo(() => Array.from({ length: ROW_COUNT }, (_, i) => i), []);

  return (
    <>
      {rows.map((rowIdx) => (
        <tr key={`skeleton_${rowIdx}`} className="h-10 border-b border-gray-200">
          {columns.map((col, colIdx) => {
            const fraction =
              SKELETON_WIDTHS[rowIdx % SKELETON_WIDTHS.length]?.[
                colIdx % SKELETON_WIDTHS[0].length
              ] || 0.5;

            return (
              <td
                key={`skeleton_${rowIdx}_${col.selector}`}
                className={`px-3 border-r border-r-gray-200 last:border-r-0 ${
                  col.isPinned ? "bg-white" : ""
                }`}
                style={{
                  width: col.width,
                  minWidth: col.width,
                  overflow: col.isPinned ? "visible" : undefined,
                  ...col.pinnedStyle,
                }}
              >
                <div className="flex items-center h-full py-2">
                  {col.selector === "name" && (
                    <div className="w-4 h-4 bg-gray-100 rounded mr-3 shrink-0 animate-pulse" />
                  )}
                  <div
                    className="h-4 bg-gray-100 rounded animate-pulse"
                    style={{
                      width: `${Math.round(fraction * 100)}%`,
                      animationDelay: `${rowIdx * 75 + colIdx * 50}ms`,
                    }}
                  />
                </div>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
};

export default React.memo(SkeletonRows);
